import { buildLoginRedirectPath, isPublicRoute } from "@/app/public-pages";
import * as Sentry from "@sentry/nextjs";
import { shouldUseMockData } from "@/lib/mock-data/utils";
import {
  getApiBaseUrl,
  getApiV2BaseUrl,
  getProxiedApiBaseUrl,
  getProxiedApiV2BaseUrl,
} from "@/lib/api-config";
import { useUnifiedProfileStore } from "@/store/use-unified-profile-store";
import { Session } from "next-auth";
import { getSession, signOut } from "next-auth/react";
import {
  IMPERSONATION_TOAST,
  isImpersonationReadOnly,
} from "@/lib/impersonation";
import { toast } from "@/lib/toast";

// =============================================================================
// Types
// =============================================================================

/**
 * API version to use for the request.
 * - v1: Legacy API (/api/*) - removed
 * - v2: FastAPI (/api/v2/*)
 */
export type ApiVersion = "v1" | "v2";

export interface ApiErrorResponse {
  message: string;
  code?: string;
  details?: unknown;
}

export type ApiResponse<T> = {
  data: T;
  status: number;
  error?: ApiErrorResponse;
};

export type ApiSuccessResponse<T> = Omit<ApiResponse<T>, "error"> & {
  success: boolean;
  error?: string;
};

export type RequestOptions = {
  params?: Record<string, any>;
  body?: any;
  headers?: Record<string, string>;
  trailingSlash?: boolean;
  signal?: AbortSignal;
  timeoutMs?: number;
  /**
   * API version to use. Defaults to auto-detection based on endpoint prefix.
   * - v1: Legacy API (/api/*) - removed
   * - v2: FastAPI (/api/v2/*) - no trailing slashes
   */
  version?: ApiVersion;
  /**
   * HTTP error statuses that are part of the endpoint's CONTRACT, not
   * failures (e.g. tour/progress 404 = "no progress row yet", SIG-898).
   * The request still rejects with ApiClientError — callers handle it — but
   * the client skips its console.error + Sentry report for these statuses so
   * a by-design response doesn't render as ~21 network errors per page load
   * (SIG-900). Scope this to exact statuses; never use it to blanket-silence.
   */
  expectedErrorStatuses?: number[];
};

// For backward compatibility with base-api-client
export interface ApiRequestConfig {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  endpoint: string;
  data?: any;
  params?: Record<string, string | number | boolean>;
  headers?: Record<string, string>;
  timeout?: number;
}

/**
 * SIG-212 Slice 2: public auth endpoints that must NOT go through the BFF
 * proxy. They are unauthenticated (no Bearer to attach) and the proxy denies
 * `api/v2/auth/**` on purpose, so client-side they call the backend directly.
 * The normalized endpoint (leading slash + `v2/`/`api/` prefixes stripped) is
 * matched, so both `/auth/forgot-password` and `/api/v2/auth/forgot-password`
 * forms resolve here.
 */
const PUBLIC_NO_PROXY_AUTH = [
  "auth/forgot-password",
  "auth/reset-password",
  "auth/verify-email",
  "auth/resend-verification",
];
const isPublicNoProxyEndpoint = (endpoint: string): boolean => {
  const normalized = endpoint
    .replace(/^\/+/, "")
    .replace(/^api\//, "")
    .replace(/^v2\//, "");
  if (PUBLIC_NO_PROXY_AUTH.some((p) => normalized.startsWith(p))) return true;
  // Email-exists probe: `auth/users/{email}/exists` (v2 auth router, public).
  return /^auth\/users\/[^/]+\/exists\/?$/.test(normalized);
};

const isNextAuthRequest = (endpoint: string): boolean => {
  return endpoint.startsWith("/api/auth/");
};

/**
 * Error thrown for non-OK API responses (SIG-167).
 *
 * Carries the HTTP `status` and the raw backend `detail` so error toasts can
 * surface the human-readable backend validation message ("Already a leader of
 * another active group", tier limits, env mismatches, …) instead of a generic
 * "API request failed".
 */
export class ApiClientError extends Error {
  readonly status: number;
  readonly detail: unknown;
  readonly responseData: unknown;
  /** Seconds from a numeric Retry-After response header (429s), else null. */
  readonly retryAfterSeconds: number | null;

  constructor(
    message: string,
    status: number,
    detail: unknown,
    responseData: unknown = detail,
    retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.detail = detail;
    this.responseData = responseData;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Extract a human-readable message from a backend error body (SIG-167).
 *
 * Handles every error envelope our FastAPI backend emits:
 * - `{ detail: "plain string" }` (HTTPException)
 * - `{ detail: [{ msg, loc }, …] }` (422 validation errors — previously
 *   collapsed to "[object Object]")
 * - `{ detail: { message | detail: … } }` (nested envelopes)
 * - `{ message: … }` / `{ non_field_errors: […] }` (legacy shapes)
 * - Structured tier/limit envelopes with no message text —
 *   `{ error | error_code, resource?, current|used?, max|limit?,
 *   upgrade_url? }` (dependencies/tier_limits.py + middleware/tier_limits.py)
 *   — synthesized into "Tier limit exceeded for groups (3/3) — upgrade to
 *   continue."
 *
 * Returns null when no readable message exists so the caller can fall back to
 * the generic message.
 */
export function extractApiErrorMessage(data: unknown): string | null {
  if (data == null) return null;
  if (typeof data === "string") return data.trim() || null;
  if (typeof data !== "object") return null;

  const record = data as Record<string, unknown>;

  const fromValue = (value: unknown): string | null => {
    if (typeof value === "string") return value.trim() || null;
    if (Array.isArray(value)) {
      const parts = value
        .map((item) => {
          if (typeof item === "string") return item.trim();
          if (item && typeof item === "object") {
            const entry = item as Record<string, unknown>;
            const msg =
              typeof entry.msg === "string"
                ? entry.msg
                : typeof entry.message === "string"
                  ? entry.message
                  : null;
            if (!msg) return null;
            // FastAPI 422 items carry the offending field in `loc`
            const loc = Array.isArray(entry.loc)
              ? entry.loc.filter((l) => typeof l === "string" && l !== "body")
              : [];
            return loc.length > 0 ? `${loc.join(".")}: ${msg}` : msg;
          }
          return null;
        })
        .filter((part): part is string => Boolean(part));
      return parts.length > 0 ? parts.join("; ") : null;
    }
    if (value && typeof value === "object") {
      const nested = value as Record<string, unknown>;
      return (
        fromValue(nested.message) ??
        fromValue(nested.detail) ??
        fromStructuredEnvelope(nested)
      );
    }
    return null;
  };

  // Tier/limit rejections carry machine fields but no message text — build a
  // human-readable line from the error code + usage numbers.
  const fromStructuredEnvelope = (
    envelope: Record<string, unknown>,
  ): string | null => {
    const code = [envelope.error, envelope.error_code, envelope.code].find(
      (value): value is string => typeof value === "string" && value.trim().length > 0,
    );
    if (!code) return null;

    const label = code
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase());
    const resource =
      typeof envelope.resource === "string" ? ` for ${envelope.resource}` : "";
    const current = envelope.current ?? envelope.used;
    const max = envelope.max ?? envelope.limit;
    const usage =
      typeof current === "number" && typeof max === "number"
        ? ` (${current}/${max})`
        : "";
    const upgrade =
      envelope.upgrade_required === true ||
      typeof envelope.upgrade_url === "string"
        ? " — upgrade to continue."
        : "";
    const accountIds = Array.isArray(envelope.account_ids)
      ? envelope.account_ids.filter(
          (value): value is number =>
            typeof value === "number" && Number.isSafeInteger(value) && value > 0,
        )
      : [];
    const affected = accountIds.length > 0
      ? `. Affected account IDs: ${accountIds.join(", ")}.`
      : "";
    return `${label}${resource}${usage}${upgrade}${affected}`;
  };

  return (
    fromValue(record.message) ??
    fromValue(record.detail) ??
    fromValue(record.non_field_errors) ??
    fromStructuredEnvelope(record)
  );
}

type SentryTaggedError = Error & { __sentryReported?: boolean };

class ApiClient {
  private baseUrl: string;
  private v2BaseUrl: string;
  private trailingSlashEndpoints = [
    // remove this
    "users/token",
    "users/register",
    "accounts",
  ];

  private lastSessionError: number = 0;
  private cachedSession: Session | null = null;
  private sessionLastFetched: number = 0;
  private sessionCacheDuration: number = 180000; // 3 minutes (was 30 seconds)
  private isSigningOut: boolean = false; // Flag to manage sign-out process
  private ongoingGetSessionPromise: Promise<Session | null> | null = null;
  private ongoingReauthenticationPromise: Promise<void> | null = null;

  public getIsSigningOutStatus(): boolean {
    // Getter for isSigningOut
    return this.isSigningOut;
  }

  // Add method to clear session cache (for post-login session refresh)
  public clearSessionCache(): void {
    this.cachedSession = null;
    this.sessionLastFetched = 0;
    this.ongoingGetSessionPromise = null;
  }

  private invalidateCachedSession(): void {
    this.cachedSession = null;
    this.sessionLastFetched = 0;
  }

  private async getSessionSingleFlight(): Promise<Session | null> {
    if (this.ongoingGetSessionPromise) {
      return this.ongoingGetSessionPromise;
    }

    const request = getSession();
    this.ongoingGetSessionPromise = request;
    try {
      return await request;
    } finally {
      if (this.ongoingGetSessionPromise === request) {
        this.ongoingGetSessionPromise = null;
      }
    }
  }

  private beginReauthentication(
    redirectTarget: string,
    recoveredSession: Session | null,
  ): void {
    if (this.ongoingReauthenticationPromise) return;

    const recovery = (async () => {
      this.invalidateCachedSession();

      if (recoveredSession && !recoveredSession.error) {
        this.cachedSession = recoveredSession;
        this.sessionLastFetched = Date.now();
        return;
      }

      // Null and RefreshAccessTokenError can be transient while Auth.js is
      // rotating the cookie. Only its explicit terminal verdict may destroy
      // a paying member's session.
      if (recoveredSession?.error !== "RefreshTokenExpired") return;
      if (this.isSigningOut) return;

      this.isSigningOut = true;
      try {
        await signOut({
          redirect: true,
          callbackUrl: buildLoginRedirectPath(redirectTarget),
        });
      } catch (error) {
        console.error(
          "[ApiClient] Failed to route to re-authentication:",
          error,
        );
      } finally {
        setTimeout(() => {
          this.isSigningOut = false;
        }, 5000);
      }
    })();

    this.ongoingReauthenticationPromise = recovery;
    void recovery.finally(() => {
      if (this.ongoingReauthenticationPromise === recovery) {
        this.ongoingReauthenticationPromise = null;
      }
    });
  }

  constructor() {
    // Use centralized API config - single source of truth for all API URLs
    this.baseUrl = getApiBaseUrl();
    this.v2BaseUrl = getApiV2BaseUrl();
  }

  private reportError(error: unknown, context: Record<string, unknown>): void {
    if (error instanceof Error) {
      (error as SentryTaggedError).__sentryReported = true;
    }
    Sentry.captureException(error, {
      tags: {
        component: "api-client",
      },
      extra: context,
    });
  }

  /**
   * Determine if a request should use v2 API.
   * @param endpoint - The endpoint path
   * @param requestVersion - Explicit version override from request options
   * @returns True if v2 should be used
   *
   * PRD-F2-09: Default to v2 (FastAPI) since legacy v1 has been removed.
   * All API routes are now at /api/v2/* in FastAPI.
   */
  private shouldUseV2(_endpoint: string, requestVersion?: ApiVersion): boolean {
    // Explicit version override takes precedence
    if (requestVersion === "v2") return true;
    if (requestVersion === "v1") return false;

    // PRD-F2-09: Default to v2 (FastAPI) - legacy v1 removed
    return true;
  }

  private async getAuthHeaders(
    endpoint: string,
  ): Promise<Record<string, string>> {
    const defaultHeaders: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const isAuthEndpoint = [
      "/users/token/",
      "/users/register/",
      "/users/token/refresh/",
    ].includes(endpoint);

    // Email-exists check is public (no auth needed) — bypass session logic
    if (
      isAuthEndpoint ||
      (endpoint.includes("/users/") && endpoint.includes("/exists"))
    ) {
      return defaultHeaders;
    }

    // Subscription endpoints authenticate like every other endpoint: the
    // caller's session JWT. Never send an API key from the client bundle —
    // NEXT_PUBLIC_* values are public, and the backend only trusts the
    // server-only SIGMAVUE_API_KEY for true service-to-service calls.

    const { selectedProfileId, activeTradingProfileId } =
      useUnifiedProfileStore.getState();

    // Deterministic context headers: never send both profile headers.
    // v2 trading context takes precedence when set; otherwise use v1 legacy context.
    const profileContextHeaders: Record<string, string> = {};
    if (activeTradingProfileId) {
      profileContextHeaders["X-Trading-Profile-ID"] = activeTradingProfileId;
    } else if (selectedProfileId && selectedProfileId !== 0) {
      profileContextHeaders["X-PROFILE-ID"] = String(selectedProfileId);
    }
    // Client-side
    if (typeof window !== "undefined") {
      try {
        const now = Date.now();
        let sessionToUse = this.cachedSession;
        let forceRefresh = false;
        const currentPath = window.location.pathname;
        const redirectTarget = `${currentPath}${window.location.search}`;
        const isPublicPage = isPublicRoute(currentPath);
        const hasPendingTradovateCallback =
          (currentPath === "/dashboard" ||
            currentPath === "/tradovate/callback") &&
          new URLSearchParams(window.location.search).has("tradovate_key");

        // Check if cached session exists and its access token might be expired or close to expiry
        if (
          this.cachedSession &&
          ((this.cachedSession.validity?.valid_until &&
            this.cachedSession.validity.valid_until * 1000 < now + 60000) || // Expired or expires in the next 60s
            !this.cachedSession.validity?.valid_until)
        ) {
          forceRefresh = true;
        }

        // Determine if we need to fetch a new session
        const needsNewSession =
          forceRefresh ||
          !sessionToUse ||
          now - this.sessionLastFetched > this.sessionCacheDuration;

        if (needsNewSession) {
          sessionToUse = await this.getSessionSingleFlight();

          if (!sessionToUse && !isPublicPage) {
            // In mock mode, don't force sign-out on transient null sessions
            // This allows demo/mock flows to work without auth interruptions
            if (shouldUseMockData() || hasPendingTradovateCallback) {
              if (hasPendingTradovateCallback) {
                console.warn(
                  "[ApiClient] Session temporarily unavailable during Tradovate callback; skipping forced sign-out.",
                );
              }
              return {
                ...defaultHeaders,
                ...profileContextHeaders,
              };
            }

            // This should not be performed on public pages
            if (!this.isSigningOut) {
              console.warn(
                "[ApiClient] getSession() returned null; attempting session recovery.",
              );
              this.beginReauthentication(redirectTarget, sessionToUse);
            }
            return defaultHeaders;
          }
          this.cachedSession = sessionToUse;
          this.sessionLastFetched = now; // Update last fetched time only after successful fetch
        } else if (sessionToUse) {
          // console.log('API Client: Using cached session.');
        }

        return {
          ...defaultHeaders,
          ...(sessionToUse?.accessToken
            ? { Authorization: `Bearer ${sessionToUse.accessToken}` }
            : {}),
          ...profileContextHeaders,
        };
      } catch (error) {
        // Ensure ongoingGetSessionPromise is cleared if an error occurs within the try block before it's naturally cleared
        this.ongoingGetSessionPromise = null;
        console.error("[ApiClient] Error getting client session:", error);
        this.reportError(error, {
          stage: "client-session",
          endpoint,
        });
        return defaultHeaders;
      }
    }

    // Server-side with caching
    try {
      // Return default headers if we've failed to get a session recently
      if (this.lastSessionError && Date.now() - this.lastSessionError < 5000) {
        return defaultHeaders;
      }

      // Check if we're in a build environment (static generation)
      // During build time, we can't access dynamic server features like headers
      if (process.env.NODE_ENV === "production" && !process.env.RUNTIME) {
        return defaultHeaders;
      }

      // Auth.js v5: Use auth() from centralized config
      const { auth } = await import("@/auth");
      const session = await auth();
      if (!session) {
        this.lastSessionError = Date.now();
        return defaultHeaders;
      }

      return {
        ...defaultHeaders,
        ...(session?.accessToken
          ? { Authorization: `Bearer ${session.accessToken}` }
          : {}),
        ...profileContextHeaders,
      };
    } catch (error) {
      console.error("[ApiClient] Error getting server session:", error);
      this.reportError(error, {
        stage: "server-session",
        endpoint,
      });
      this.lastSessionError = Date.now();
      return defaultHeaders;
    }
  }

  private buildUrl(
    endpoint: string,
    params?: Record<string, any>,
    trailingSlash?: boolean,
    version?: ApiVersion,
  ): URL {
    const useV2 = this.shouldUseV2(endpoint, version);

    // Remove leading slashes and reduce multiple trailing slashes to a single slash.
    let cleanEndpoint = endpoint
      .replace(/^\/+/g, "") // Remove leading slashes
      .replace(/\/{2,}$/g, "/"); // Reduce multiple trailing slashes to one

    // For v2, strip the v2/ prefix since it's in the base URL
    if (useV2) {
      cleanEndpoint = cleanEndpoint.replace(/^v2\//, "");
    }

    let finalPath: string;
    let baseUrl: string;
    // SIG-212 Slice 2: client-side requests route through the same-origin
    // BFF proxy (which attaches the backend token server-side from the
    // httpOnly cookie). Server-side requests keep the direct backend origin.
    // Public auth endpoints bypass the proxy (unauthenticated + proxy-denied)
    // and always hit the backend directly.
    const isClientSide = typeof window !== "undefined";
    const useProxy = isClientSide && !isPublicNoProxyEndpoint(endpoint);
    const freshBaseUrl = useProxy ? getProxiedApiBaseUrl() : getApiBaseUrl();
    const freshV2BaseUrl = useProxy
      ? getProxiedApiV2BaseUrl()
      : getApiV2BaseUrl();

    if (useV2) {
      // PRD-F2-09: Strip leading api/ prefix since v2BaseUrl already has /api/v2
      // This prevents double /api/v2/api/ paths when hooks use /api/... style endpoints
      let pathForV2 = cleanEndpoint;
      if (pathForV2.startsWith("api/")) {
        pathForV2 = pathForV2.slice(4); // Remove 'api/' prefix
      }
      // Honor the endpoint's trailing slash as written. FastAPI declares
      // routes with explicit trailing slashes; stripping the slash the caller
      // wrote forces a 307 redirect on every request — and turned the OAuth
      // POST /tradovate/complete/ into a redirected duplicate that 400'd with
      // "Authorization code already used" (SIG-106). `trailingSlash: true`
      // still forces a slash; `trailingSlash: false` still strips it.
      if (trailingSlash === true) {
        finalPath = `${pathForV2.replace(/\/+$/, "")}/`;
      } else if (trailingSlash === false) {
        finalPath = pathForV2.replace(/\/+$/, "");
      } else {
        finalPath = pathForV2;
      }
      baseUrl = freshV2BaseUrl;
    } else {
      // v1 endpoints: baseUrl already contains /api, so we only need to add api/ prefix
      // if the endpoint starts with api/ (explicit) or contains subscriptions (legacy)
      // AND the baseUrl doesn't already end with /api
      const baseUrlHasApi = freshBaseUrl.endsWith("/api");

      // Strip api/ prefix from endpoint if baseUrl already has /api
      let pathForUrl = cleanEndpoint;
      if (baseUrlHasApi && cleanEndpoint.startsWith("api/")) {
        pathForUrl = cleanEndpoint.slice(4); // Remove 'api/' prefix
      } else if (
        !baseUrlHasApi &&
        !cleanEndpoint.startsWith("api/") &&
        !cleanEndpoint.includes("subscriptions")
      ) {
        // Only add api/ prefix if baseUrl doesn't have /api and endpoint doesn't have it
        pathForUrl = `api/${cleanEndpoint}`;
      }

      // Determine the final path based on the trailingSlashEndpoints list
      finalPath = this.trailingSlashEndpoints.includes(cleanEndpoint)
        ? pathForUrl.endsWith("/")
          ? pathForUrl
          : `${pathForUrl}/` // Add single trailing slash if in the list
        : pathForUrl; // No trailing slash if not in the list
      baseUrl = freshBaseUrl;
    }

    const url = new URL(finalPath, baseUrl + "/");

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url;
  }

  // Default request timeout in milliseconds (15 seconds)
  private static readonly DEFAULT_TIMEOUT_MS = 15000;

  private async request<T>(
    method: string,
    endpoint: string,
    {
      params,
      body,
      headers,
      trailingSlash,
      signal,
      timeoutMs,
      version,
      expectedErrorStatuses,
    }: RequestOptions = {},
    _retriedAuth = false,
  ): Promise<ApiResponse<T>> {
    // =========================================================================
    // DEMO MODE SHORT-CIRCUIT
    // =========================================================================
    // In demo mode, return instant mock responses instead of waiting for
    // network timeouts. This prevents 15+ second page navigation delays.
    // Auth/session endpoints bypass this to allow demo login to work.
    // =========================================================================
    const lowerEndpoint = endpoint.toLowerCase();
    const isFinvizEndpoint =
      lowerEndpoint.includes("/finviz/") || lowerEndpoint.startsWith("finviz/");

    if (shouldUseMockData() && !isFinvizEndpoint) {
      // FE-1 item 3: the 2k-line mock-responses catalog loads only when demo
      // mode is actually on — never in the real-data prod graph. Same
      // short-circuit semantics as before: shouldBypassDemoMode was only ever
      // evaluated behind shouldUseMockData().
      const { getMockResponseForEndpoint, shouldBypassDemoMode } = await import(
        "@/lib/mock-data/mock-responses"
      );
      if (!shouldBypassDemoMode(endpoint)) {
        const mockData = getMockResponseForEndpoint(endpoint, method, body);
        return {
          data: mockData as T,
          status: 200,
        };
      }
    }

    // PERF: Add timeout to prevent hung requests from blocking forever
    // Creates an AbortController with 15s timeout if no signal provided
    let timeoutId: NodeJS.Timeout | undefined;
    let abortController: AbortController | undefined;
    let effectiveSignal = signal;

    const requestTimeoutMs = timeoutMs ?? ApiClient.DEFAULT_TIMEOUT_MS;

    if (!signal) {
      abortController = new AbortController();
      effectiveSignal = abortController.signal;
      timeoutId = setTimeout(() => {
        abortController?.abort();
        console.warn(
          `[ApiClient] Request timeout after ${requestTimeoutMs}ms:`,
          endpoint,
        );
      }, requestTimeoutMs);
    }

    try {
      const isNextAuth = isNextAuthRequest(endpoint);

      const url = this.buildUrl(endpoint, params, trailingSlash, version);
      let authHeaders = await this.getAuthHeaders(endpoint);

      // if the endpoint is for checking if an email exists, do not include the auth headers
      if (url.toString().includes("/exists/")) {
        authHeaders = {
          "Content-Type": "application/json",
        };
      }

      const response = await fetch(url.toString(), {
        method,
        headers: {
          ...authHeaders,
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        ...(isNextAuth ? { credentials: "include" } : {}),
        ...(effectiveSignal ? { signal: effectiveSignal } : {}),
      });

      // SIG-212 Slice 2: the BFF proxy never refreshes tokens — it signals an
      // expired access token with 401 + X-Auth-Error: TokenExpired. Refresh
      // the session once (NextAuth's jwt callback rotates + persists the
      // cookie via /api/auth/session) and retry the request one time.
      if (
        response.status === 401 &&
        typeof window !== "undefined" &&
        !_retriedAuth &&
        response.headers.get("x-auth-error") === "TokenExpired"
      ) {
        if (timeoutId) clearTimeout(timeoutId);
        this.invalidateCachedSession();
        let refreshedSession: Session | null = null;
        try {
          refreshedSession = await this.getSessionSingleFlight();
        } catch {
          // The original 401 is parsed below. A hard refresh failure must not
          // retry either the refresh endpoint or the original API request.
        }
        const refreshFailed =
          !refreshedSession || Boolean(refreshedSession.error);
        if (refreshFailed) {
          const redirectTarget = `${window.location.pathname}${window.location.search}`;
          this.beginReauthentication(redirectTarget, refreshedSession);
        } else {
          this.cachedSession = refreshedSession;
          this.sessionLastFetched = Date.now();
          return this.request<T>(
            method,
            endpoint,
            {
              params,
              body,
              headers,
              trailingSlash,
              signal,
              timeoutMs,
              version,
              expectedErrorStatuses,
            },
            true,
          );
        }
      }

      const nonJsonEndpoints = ["/users/token/logout/"];

      // Some endpoints intentionally return no body (e.g. 204 No Content).
      // Avoid JSON parse errors that surface as noisy console errors.
      let data: any = null;
      if (response.status !== 204) {
        try {
          data = nonJsonEndpoints.includes(endpoint)
            ? await response.text()
            : await response.json();
        } catch (error) {
          console.error("Error parsing response:", error);
          data = { message: "No response data" };
        }
      }

      /* if (endpoint.includes('/users/token/')) {
        console.log(`Request ${method} made to:`, url.toString());
        console.log('Request headers:', authHeaders);
        console.log('Request body:', body);
        console.log('Request options:', { params, body, headers });
        console.log('Response status:', response.status);
        console.log('Response data:', data);
      } */

      if (!response.ok) {
        // Broker-scoped 401s still emit the reconnect signal, but every
        // non-OK response below rejects. Returning 401/403/404 as data made
        // TanStack Query cache authorization and missing-resource failures as
        // successful results, which flattened live surfaces into empty state.
        if (response.status === 401) {
          /* if (typeof window !== 'undefined' && !this.isSigningOut) {
            this.isSigningOut = true;
            console.warn('[ApiClient] Detected 401, initiating sign-out.');
            signOut({ redirect: true, callbackUrl: '/' })
              .catch(err => {
                console.error('[ApiClient] Error during signOut:', err);
              })
              .finally(() => {
                // Reset the flag after a delay to allow redirect and prevent immediate re-triggering
                setTimeout(() => {
                  this.isSigningOut = false;
                  console.log('[ApiClient] isSigningOut flag reset.');
                }, 5000); // 5-second delay
              });
          } else if (this.isSigningOut) {
            console.log('[ApiClient] Sign-out already in progress, returning 401.');
          } */
          notifyBrokerTokenExpired(data);
        }

        // Clear timeout before throwing generic error
        if (timeoutId) clearTimeout(timeoutId);
        // SIG-167: surface the backend's human-readable detail (incl. 422
        // validation arrays and nested envelopes) so error toasts show WHY the
        // request failed. Falls back to the generic message only when the body
        // carries no readable detail.
        // Numeric Retry-After only (broker 429 budget); HTTP-date form is
        // ignored rather than mis-parsed.
        const retryAfterHeader = response.headers.get("retry-after")?.trim();
        const retryAfterSeconds =
          retryAfterHeader && /^\d+$/.test(retryAfterHeader)
            ? Number(retryAfterHeader)
            : null;
        if (response.status === 403 && isImpersonationReadOnly(data?.detail ?? data)) {
          toast.error(IMPERSONATION_TOAST, { id: "impersonation-read-only" });
        }
        throw new ApiClientError(
          extractApiErrorMessage(data) ?? "API request failed",
          response.status,
          data?.detail ?? data,
          data,
          retryAfterSeconds,
        );
      }

      // Clear timeout on successful response
      if (timeoutId) clearTimeout(timeoutId);
      return { data, status: response.status };
    } catch (error) {
      // Clear timeout on error
      if (timeoutId) clearTimeout(timeoutId);

      // Provide clearer error for timeout/abort
      if (error instanceof Error && error.name === "AbortError") {
        const timeoutError = new Error(`Request timeout: ${endpoint}`);
        console.error("[ApiClient] Request aborted (timeout):", {
          endpoint,
          method,
        });
        this.reportError(timeoutError, {
          stage: "timeout",
          endpoint,
          method,
        });
        throw timeoutError;
      }

      // SIG-900: a status the caller declared as part of the endpoint's
      // contract (e.g. 404 = "no row yet") is not an error to log or report —
      // rethrow quietly and let the caller's mapping handle it.
      if (
        error instanceof ApiClientError &&
        expectedErrorStatuses?.includes(error.status)
      ) {
        throw error;
      }

      // Self-describing first argument: the dev overlay and log collectors
      // render objects as "{}" (Error props are non-enumerable), which made
      // every failure show as "[ApiClient] Error: {}" with no signal.
      console.error(
        `[ApiClient] ${method} ${endpoint} failed: ${
          error instanceof Error ? error.message : String(error)
        }`,
        error,
      );
      this.reportError(error, {
        stage: "request",
        endpoint,
        method,
      });
      throw error;
    }
  }

  async get<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>("GET", endpoint, options);
  }

  async post<T>(
    endpoint: string,
    body?: any,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>("POST", endpoint, { ...options, body });
  }

  async put<T>(
    endpoint: string,
    body?: any,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>("PUT", endpoint, { ...options, body });
  }

  async patch<T>(
    endpoint: string,
    body?: any,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>("PATCH", endpoint, { ...options, body });
  }

  async delete<T>(
    endpoint: string,
    body?: any,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>("DELETE", endpoint, { ...options, body });
  }

  // Adapter methods for base-api-client compatibility
  // These wrap the responses to match the { success, data, error } format
  async requestWithSuccess<T>(
    method: string,
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<ApiSuccessResponse<T>> {
    try {
      const { data, status } = await this.request<T>(method, endpoint, options);
      return { data, status, success: true };
    } catch (error) {
      return {
        data:
          error instanceof ApiClientError
            ? (error.responseData as T)
            : (null as T),
        status: error instanceof ApiClientError ? error.status : 500,
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Convenience methods with base-api-client compatible responses
  async getWithSuccess<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<ApiSuccessResponse<T>> {
    return this.requestWithSuccess<T>("GET", endpoint, options);
  }

  async postWithSuccess<T>(
    endpoint: string,
    body?: any,
    options: RequestOptions = {},
  ): Promise<ApiSuccessResponse<T>> {
    return this.requestWithSuccess<T>("POST", endpoint, { ...options, body });
  }

  async putWithSuccess<T>(
    endpoint: string,
    body?: any,
    options: RequestOptions = {},
  ): Promise<ApiSuccessResponse<T>> {
    return this.requestWithSuccess<T>("PUT", endpoint, { ...options, body });
  }

  async patchWithSuccess<T>(
    endpoint: string,
    body?: any,
    options: RequestOptions = {},
  ): Promise<ApiSuccessResponse<T>> {
    return this.requestWithSuccess<T>("PATCH", endpoint, { ...options, body });
  }

  async deleteWithSuccess<T>(
    endpoint: string,
    body?: any,
    options: RequestOptions = {},
  ): Promise<ApiSuccessResponse<T>> {
    return this.requestWithSuccess<T>("DELETE", endpoint, { ...options, body });
  }

  // ===========================================================================
  // V2 API Methods (FastAPI endpoints)
  // ===========================================================================

  /**
   * Make a request explicitly to v2 API.
   * Use this when you want to ensure the request goes to FastAPI.
   */
  async v2<T>(
    method: string,
    endpoint: string,
    options: Omit<RequestOptions, "version"> = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>(method, endpoint, { ...options, version: "v2" });
  }

  /**
   * GET request to v2 API.
   */
  async getV2<T>(
    endpoint: string,
    options: Omit<RequestOptions, "version"> = {},
  ): Promise<ApiResponse<T>> {
    return this.get<T>(endpoint, { ...options, version: "v2" });
  }

  /**
   * POST request to v2 API.
   */
  async postV2<T>(
    endpoint: string,
    body?: any,
    options: Omit<RequestOptions, "version"> = {},
  ): Promise<ApiResponse<T>> {
    return this.post<T>(endpoint, body, { ...options, version: "v2" });
  }

  /**
   * PUT request to v2 API.
   */
  async putV2<T>(
    endpoint: string,
    body?: any,
    options: Omit<RequestOptions, "version"> = {},
  ): Promise<ApiResponse<T>> {
    return this.put<T>(endpoint, body, { ...options, version: "v2" });
  }

  /**
   * PATCH request to v2 API.
   */
  async patchV2<T>(
    endpoint: string,
    body?: any,
    options: Omit<RequestOptions, "version"> = {},
  ): Promise<ApiResponse<T>> {
    return this.patch<T>(endpoint, body, { ...options, version: "v2" });
  }

  /**
   * DELETE request to v2 API.
   */
  async deleteV2<T>(
    endpoint: string,
    body?: any,
    options: Omit<RequestOptions, "version"> = {},
  ): Promise<ApiResponse<T>> {
    return this.delete<T>(endpoint, body, { ...options, version: "v2" });
  }

  async getV2WithSuccess<T>(
    endpoint: string,
    options: Omit<RequestOptions, "version"> = {},
  ): Promise<ApiSuccessResponse<T>> {
    return this.getWithSuccess<T>(endpoint, { ...options, version: "v2" });
  }

  async postV2WithSuccess<T>(
    endpoint: string,
    body?: any,
    options: Omit<RequestOptions, "version"> = {},
  ): Promise<ApiSuccessResponse<T>> {
    return this.postWithSuccess<T>(endpoint, body, {
      ...options,
      version: "v2",
    });
  }
}

export const apiClient = new ApiClient();

if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  (window as any).apiClient = apiClient; // for debugging purposes
}

// --- Broker token-expiry surfacing (issue #285) -------------------------------
// A broker-scoped 401 ("No valid token for <env>. Please re-authenticate.")
// must become a visible reconnect prompt, not a silent failure. The client is
// framework-free, so it emits a window event; BrokerTokenExpiryListener (in
// the app layout) renders the toast with the Reconnect action.

export const BROKER_TOKEN_EXPIRED_EVENT = "sigmavue:broker-token-expired";
const BROKER_401_PATTERN = /no valid token for (demo|live)/i;
const brokerExpiryLastNotified: Record<string, number> = {};
const BROKER_EXPIRY_THROTTLE_MS = 60_000;

export function notifyBrokerTokenExpired(errorBody: unknown): void {
  if (typeof window === "undefined") return;
  const detail =
    typeof errorBody === "object" && errorBody !== null
      ? String((errorBody as { detail?: unknown }).detail ?? "")
      : String(errorBody ?? "");
  const match = BROKER_401_PATTERN.exec(detail);
  if (!match) return;
  const environment = match[1].toLowerCase() as "demo" | "live";
  const now = Date.now();
  if (
    now - (brokerExpiryLastNotified[environment] ?? 0) <
    BROKER_EXPIRY_THROTTLE_MS
  ) {
    return;
  }
  brokerExpiryLastNotified[environment] = now;
  const payload = { environment, message: detail };
  pendingBrokerExpiries.set(environment, payload);
  window.dispatchEvent(
    new CustomEvent(BROKER_TOKEN_EXPIRED_EVENT, { detail: payload }),
  );
}

// 401s can land during initial hydration, before the listener mounts (review
// P1, PR 290). The listener drains this buffer on mount so nothing is dropped.
const pendingBrokerExpiries = new Map<
  string,
  { environment: "demo" | "live"; message: string }
>();

export function clearPendingBrokerExpiry(environment: string): void {
  pendingBrokerExpiries.delete(environment);
}

export function drainPendingBrokerExpiries(): Array<{
  environment: "demo" | "live";
  message: string;
}> {
  // Array.from avoids TS2802 (MapIterator spread needs --downlevelIteration)
  const pending = Array.from(pendingBrokerExpiries.values());
  pendingBrokerExpiries.clear();
  return pending;
}
